import { useCallback, useMemo, useRef, useState } from 'react'
import type { JSX, ReactNode } from 'react'
import { ToastContext } from './toast-context'
import type { Toast, ToastApi } from './toast-context'

/** How long a toast stays before it removes itself. */
const DISMISS_AFTER_MS = 6000

/**
 * The toast host.
 *
 * One live region for the whole app (`.ar-toasts`, S-D1), announced politely so
 * a screen reader hears "Saved" without losing the caret. Anything the user
 * must act on is not a toast — it is an inline error or a modal — because a
 * toast that disappears after six seconds is the wrong place for a decision.
 *
 * Timers are tracked so unmounting clears them; a stray timeout firing into a
 * removed tree is the classic React warning, and here it would also leak one
 * timer per toast for the life of the tab.
 */
export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([])
  const nextId = useRef(1)
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>())

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id)
    if (timer !== undefined) {
      clearTimeout(timer)
      timers.current.delete(id)
    }
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const show = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = nextId.current++
      setToasts((current) => [...current, { ...toast, id }])
      timers.current.set(
        id,
        setTimeout(() => {
          dismiss(id)
        }, DISMISS_AFTER_MS),
      )
      return id
    },
    [dismiss],
  )

  const api = useMemo<ToastApi>(() => ({ show, dismiss }), [show, dismiss])

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="ar-toasts" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`ar-toast ar-toast--${toast.kind}`}>
            <div className="ar-toast__body">
              <strong>{toast.title}</strong>
              {toast.body ? <span>{toast.body}</span> : null}
            </div>
            <button
              type="button"
              className="ar-btn ar-btn--quiet ar-btn--sm ar-btn--icon"
              aria-label={`Dismiss: ${toast.title}`}
              onClick={() => {
                dismiss(toast.id)
              }}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}
